/**
 * card-check.js
 *
 * Handles API calls to verify card availability and toggles form fields.
 */

import { setError, toggleLoader } from './form-fields';
import { loadFormData } from './form-persistence';

const {
	error_card_inactive,
	error_card_registered,
	error_card_not_found,
	error_card_expired,
	error_card_unknown,
	error_card_parameters,
} = window.crTranslations;

const ERROR_MESSAGES = {
	1: error_card_inactive,
	2: error_card_registered,
	3: error_card_not_found,
	4: error_card_expired,
	5: error_card_unknown,
	6: error_card_parameters,
};

/**
 * Initialize card check logic.
 *
 * @param {HTMLFormElement} form
 * @param {CardRegistrationAPI} api
 * @param {Record<string, HTMLElement>} elements
 */
export function initCardCheck(form, swiper, api, elements) {
	const cardNumberEl = elements.cardNumber;
	if (!cardNumberEl) return;

	const submitBtn = form.querySelector('.cr-submit-btn');

	// Helper to toggle all other fields on slide 1
	const toggleFields = (enabled) => {
		Object.entries(elements).forEach(([name, el]) => {
			if (name === 'cardNumber') return;

			// Only toggle fields on the current slide (slide 1)
			if (el.closest('.swiper-slide') === swiper.slides[0]) {
				el.disabled = !enabled;
			}
		});

		if (submitBtn) submitBtn.disabled = !enabled;
	};

	let timeout = null;
	let lastValue = cardNumberEl.value.trim();
	let isLastCheckValid = false;

	const handleCheck = async () => {
		const cardNumber = cardNumberEl.value.trim();

		// If card number is empty or less than 8 digits, just disabling fields
		if (cardNumber.length < 8) {
			toggleFields(false);
			isLastCheckValid = false;
			return;
		}

		// Optimization: Don't check if value hasn't changed
		if (cardNumber === lastValue && cardNumber.length === 8) {
			// Re-apply correct field states even if value hasn't changed
			// because they might have been disabled by updateSlideInteractivity
			if (isLastCheckValid) {
				toggleFields(true);
			}
			return;
		}

		lastValue = cardNumber;

		try {
			toggleLoader(form, true);
			cardNumberEl.disabled = true;
			cardNumberEl.blur();
			const response = await api.checkCard(cardNumber);

			// Check for Status: OK as per user request
			if (response.body?.Status === 'OK') {
				isLastCheckValid = true;
				toggleFields(true);
				setError(cardNumberEl, '');

				// Restore remaining fields from localStorage
				loadFormData(elements, ['cardNumber']);
			} else {
				isLastCheckValid = false;
				toggleFields(false);
				const resultCode = response.body?.Result;
				const errorMsg =
					ERROR_MESSAGES[resultCode] || 'Kortelės tikrinimo klaida';
				setError(cardNumberEl, errorMsg);
			}
		} catch (err) {
			console.error('[Card Check] Error:', err);
			isLastCheckValid = false;
			toggleFields(false);
			setError(
				cardNumberEl,
				'Nepavyko patikrinti kortelės. Bandykite dar kartą.'
			);
		} finally {
			toggleLoader(form, false);
			cardNumberEl.disabled = false;
		}
	};

	cardNumberEl.addEventListener('input', () => {
		clearTimeout(timeout);
		timeout = setTimeout(handleCheck, 500); // Debounce API calls
	});

	cardNumberEl.addEventListener('focus', () => {
		lastValue = cardNumberEl.value.trim();
	});

	cardNumberEl.addEventListener('blur', () => {
		if (cardNumberEl.value.trim() !== lastValue) {
			handleCheck();
		}
	});

	// Attach handleCheck to the form for centralized interactivity updates
	form.recheckCard = handleCheck;

	// On load: if card number is already valid, trigger check immediately
	if (cardNumberEl.value.trim().length === 8) {
		// Force check on load even if lastValue matches (because lastValue is initialized to value)
		lastValue = '';
		handleCheck();
	}
}
